// @flow

import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import {GetApp} from '@material-ui/icons';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../assets/styles/colors';
import HorizontalFilter from '../../../../elements/HorizontalFilter/HorizontalFilter';
import HorizontalScroll from '../../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import FilterButton from './FilterButton/FilterButton';
import RowItemProducts from './RowItem/RowItemProducts';
import {endpoints} from '../../api';
import type {ProductData, MostSeenProductData} from '../../duck';
import type {DashboardAction} from '../../duck';
import {useMemo} from 'react';
type Props = {
  canShowDisabled: boolean,
  products?: ProductData,
  isFetchingSummaryProducts?: boolean,
  mostSeenProducts?: MostSeenProductData[],
  buildCommonQueryParamsCallback: () => any,
  fetchSummaryProducts: (payload: DashboardAction) => void,
};

const useStyles = makeStyles(theme => ({
  section: {
    height: '100%',
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(2),
    borderRadius: '12px',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: colors.grey900,
  },
  divider: {
    backgroundColor: '#E8E8ED',
    margin: theme.spacing(2, 0),
  },
  btnDownload: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  seeMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  seeMore: {
    color: colors.grey800,
    fontSize: 14,
  },
  advice: {
    marginLeft: theme.spacing(1),
    fontSize: 14,
    color: colors.grey700,
  },
  wrapFilter: {
    '& > div': {
      height: '48px!important',
    },
  },
  containerProgress: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  wrapProducts: {
    position: 'relative',
  },
  chartBackdropSales: {
    backgroundColor: 'rgba(255, 255, 255, 0.6)!important',
  },
  emptyChartBackdrop: {
    zIndex: '10',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: '#ffffff10',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyChartAlert: {
    background: colors.grey200,
    borderRadius: '24px',
    width: 260,
    padding: theme.spacing(3),
    textAlign: 'center',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  chartAlertSales: {
    backgroundColor: '#FFFFFF!important',
    padding: theme.spacing(3, 2),
    boxShadow: '0px 4px 16px rgba(55, 61, 76, 0.16)',
    borderRadius: '12px',
  },
  textAlertSale: {
    color: colors.grey900,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

export default function ProductsSection(props: Props) {
  const {
    canShowDisabled,
    products,
    isFetchingSummaryProducts,
    mostSeenProducts,
    buildCommonQueryParamsCallback,
    fetchSummaryProducts,
  } = props;
  const classes = useStyles();

  const [productsFilter, setProductsFilter] = useState('best-selling');
  const [showMoreProducts, setShowMoreProducts] = useState(false);

  const buildQueryParamsCallback = useCallback(() => {
    let params: {[key: string]: any} = {
      size: 10,
      page: 0,
    };
    if (productsFilter) {
      params.type = productsFilter;
    }
    return params;
  }, [productsFilter]);

  const withoutProducts = !(
    (products && products.length) ||
    (mostSeenProducts && mostSeenProducts.length)
  );

  const shouldShowEmptyAlert = withoutProducts && canShowDisabled;

  useEffect(() => {
    const dateRange = buildCommonQueryParamsCallback();
    const queryFilter = buildQueryParamsCallback();
    if (queryFilter && queryFilter) {
      const {start_date, end_date} = dateRange;
      const productsRequest = {
        ...queryFilter,
        init_date: start_date,
        end_date,
      };
      fetchSummaryProducts({productsRequest});
    }
  }, [
    buildCommonQueryParamsCallback,
    buildQueryParamsCallback,
    fetchSummaryProducts,
  ]);

  const handleSeeMoreProducts = (v: boolean) => {
    setShowMoreProducts(prev => !prev);
  };

  const handleUpdateFilter = (value: string) => {
    if (value) {
      setProductsFilter(value);
    }
  };

  const fileDownloadUrl = useMemo(() => {
    const dateRange = buildCommonQueryParamsCallback();
    const {start_date: init_date, end_date} = dateRange;
    const endpoint =
      productsFilter === 'best-selling'
        ? endpoints.GET_SUMMARY_PRODUCTS
        : endpoints.GET_SUMMARY_MOST_SEEN_PRODUCTS;

    return `${endpoint}?init_date=${init_date}&end_date=${end_date}&format=xlsx`;
  }, [buildCommonQueryParamsCallback, productsFilter]);

  const currentProductsArray =
    productsFilter === 'most-seen' ? mostSeenProducts : products;

  const mostSeenProductsContent = mostSeenProducts
    ? mostSeenProducts.map((p, idx) => {
        if (!showMoreProducts && idx > 4) return null;
        return (
          <Grid key={`product-${idx}`} item xs={12}>
            <RowItemProducts
              id={idx}
              text="Vistas"
              imageId={p.imageId}
              name={p.name}
              number={p.views}
            />
          </Grid>
        );
      })
    : null;

  const bestSellingProductsContent = products
    ? products.map((p, idx) => {
        if (!showMoreProducts && idx > 4) return null;
        return (
          <Grid key={`product-${idx}`} item xs={12}>
            <RowItemProducts
              id={idx}
              text="Ventas"
              imageId={p.imageId}
              name={p.name}
              number={p.salesCount}
            />
          </Grid>
        );
      })
    : null;

  return (
    <div className={classes.section}>
      <div>
        <div className={classes.header}>
          <Typography className={classes.title}>Productos</Typography>
          <CustomButton
            startIcon={<GetApp />}
            className={classes.btnDownload}
            href={fileDownloadUrl}
            variant="contained"
            target="_blank"
            rel="noopener noreferrer"
          >
            Descargar
          </CustomButton>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.wrapFilter}>
          <HorizontalFilter noMargin>
            <HorizontalScroll>
              <FilterButton
                id="best-selling"
                text="Más vendidos"
                selected={productsFilter === 'best-selling'}
                handleOnClick={handleUpdateFilter}
              />
              <FilterButton
                id="most-seen"
                text="Más vistos"
                selected={productsFilter === 'most-seen'}
                handleOnClick={handleUpdateFilter}
              />
            </HorizontalScroll>
          </HorizontalFilter>
        </div>
        <div
          className={classes.wrapProducts}
          style={{minHeight: shouldShowEmptyAlert ? '10rem' : undefined}}
        >
          {shouldShowEmptyAlert && (
            <div
              className={`${classes.emptyChartBackdrop} ${classes.chartBackdropSales}`}
            >
              <div
                className={`${classes.emptyChartAlert} ${classes.chartAlertSales}`}
              >
                <Typography className={classes.textAlertSale}>
                  Necesitas mas ventas para poder generar un gráfico de progreso
                </Typography>
              </div>
            </div>
          )}
          <Grid container spacing={2}>
            {isFetchingSummaryProducts ? (
              <div className={classes.containerProgress}>
                <CircularProgress />
              </div>
            ) : currentProductsArray && currentProductsArray.length ? (
              productsFilter === 'most-seen' ? (
                mostSeenProductsContent
              ) : (
                bestSellingProductsContent
              )
            ) : (
              <Typography className={classes.advice}>
                No hay registro de productos.
              </Typography>
            )}
          </Grid>
        </div>
        {!isFetchingSummaryProducts && <Divider className={classes.divider} />}
        {currentProductsArray && currentProductsArray.length > 5 ? (
          <div className={classes.seeMoreContainer}>
            <CustomButton
              className={classes.seeMore}
              onClick={handleSeeMoreProducts}
            >
              {showMoreProducts ? 'Ver menos' : 'Ver más'}
            </CustomButton>
          </div>
        ) : null}
      </div>
    </div>
  );
}

ProductsSection.propTypes = {
  canShowDisabled: PropTypes.bool.isRequired,
  products: PropTypes.array,
  isFetchingSummaryProducts: PropTypes.bool,
  mostSeenProducts: PropTypes.array,
  buildCommonQueryParamsCallback: PropTypes.func.isRequired,
  fetchSummaryProducts: PropTypes.func.isRequired,
};
